import React, { useState, useRef, useEffect, useCallback } from 'react';
import SiteHeader from '../components/common/SiteHeader';
import Breadcrumb from '../components/common/Breadcrumb';
import VideoThumbnailCard from '../components/common/VideoThumbnailCard';
import AddVideoModal from '../components/common/AddVideoModal';
import BackButton from '../components/common/BackButton';
import SaveStatusIndicator from '../components/common/SaveStatusIndicator';
import InvitationInput from '../components/common/InvitationInput';
import galleryService from '../services/galleryService';

function NewGalleryPage() {
  const [galleryId, setGalleryId] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [addNewVideosAtBeginning, setAddNewVideosAtBeginning] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [videoLinks, setVideoLinks] = useState([]);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [activeTab, setActiveTab] = useState('videos');
  const [showAddVideoModal, setShowAddVideoModal] = useState(false);
  const nameInputRef = useRef(null);
  const isCreatingRef = useRef(false);

  useEffect(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);

  const ensureGalleryExists = useCallback(async () => {
    if (galleryId) return galleryId;
    if (isCreatingRef.current) return null;

    isCreatingRef.current = true;
    setSaveStatus('saving');

    try {
      const result = await galleryService.autoCreateGallery({
        name: name,
        description: description,
        isPrivate: isPrivate,
        addNewVideosAtBeginning: addNewVideosAtBeginning
      });
      setGalleryId(result.id);
      setSaveStatus('saved');
      return result.id;
    } catch (err) {
      setSaveStatus('error');
      return null;
    } finally {
      isCreatingRef.current = false;
    }
  }, [galleryId, name, description, isPrivate, addNewVideosAtBeginning]);

  // Title - save on blur
  const handleNameBlur = async () => {
    if (!galleryId && !name.trim()) return;

    const id = galleryId || await ensureGalleryExists();
    if (!id) return;

    setSaveStatus('saving');
    try {
      await galleryService.patchGallery(id, { name });
      setSaveStatus('saved');
    } catch (err) {
      setSaveStatus('error');
    }
  };

  // Description - save on blur
  const handleDescriptionBlur = async () => {
    if (!galleryId) return;

    setSaveStatus('saving');
    try {
      await galleryService.patchGallery(galleryId, { description });
      setSaveStatus('saved');
    } catch (err) {
      setSaveStatus('error');
    }
  };

  // Privacy toggle - save immediately
  const handlePrivacyChange = async (value) => {
    setIsPrivate(value);

    const id = galleryId || await ensureGalleryExists();
    if (!id) return;

    setSaveStatus('saving');
    try {
      await galleryService.patchGallery(id, { isPrivate: value });
      setSaveStatus('saved');
    } catch (err) {
      setSaveStatus('error');
    }
  };

  // Video order preference - save immediately
  const handleVideoOrderChange = async (value) => {
    setAddNewVideosAtBeginning(value);

    const id = galleryId || await ensureGalleryExists();
    if (!id) return;

    setSaveStatus('saving');
    try {
      await galleryService.patchGallery(id, { addNewVideosAtBeginning: value });
      setSaveStatus('saved');
    } catch (err) {
      setSaveStatus('error');
    }
  };

  // Video add - save immediately
  const handleAddVideo = async (videoData) => {
    const id = galleryId || await ensureGalleryExists();
    if (!id) return;

    setSaveStatus('saving');
    try {
      const newVideo = await galleryService.addVideo(id, videoData);
      setVideoLinks(prev => addNewVideosAtBeginning
        ? [newVideo, ...prev]
        : [...prev, newVideo]);
      setSaveStatus('saved');
    } catch (err) {
      setSaveStatus('error');
    }
  };

  const handleUpdateVideo = async (videoId, videoData) => {
    if (!galleryId) return;

    setSaveStatus('saving');
    try {
      await galleryService.updateVideo(galleryId, videoId, videoData);
      setVideoLinks(prev => prev.map(v =>
        v.id === videoId ? { ...v, ...videoData } : v
      ));
      setSaveStatus('saved');
    } catch (err) {
      setSaveStatus('error');
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!galleryId) return;
    if (!window.confirm('Are you sure you want to remove this video?')) return;

    setSaveStatus('saving');
    try {
      await galleryService.removeVideo(galleryId, videoId);
      setVideoLinks(prev => prev.filter(v => v.id !== videoId));
      setSaveStatus('saved');
    } catch (err) {
      setSaveStatus('error');
    }
  };

  // Invitation handlers
  const handleInvitationAdded = (invitation) => {
    setInvitations(prev => [...prev, invitation]);
  };

  const handleInvitationRemoved = (email) => {
    setInvitations(prev => prev.filter(inv => inv.email !== email));
  };

  const handleInvitationUpdated = (email, canEdit) => {
    setInvitations(prev => prev.map(inv =>
      inv.email === email ? { ...inv, canEdit } : inv
    ));
  };

  const tabs = [
    { id: 'videos', label: 'Videos', icon: 'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z' },
    { id: 'general', label: 'Settings', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex flex-col">
      <SiteHeader />
      <div className="p-8 max-w-6xl mx-auto w-full">
        <Breadcrumb items={[
          { label: 'My galleries', href: '/dashboard' },
          { label: 'Create new gallery' }
        ]} />

        {/* Back Button */}
        <div className="mt-4">
          <BackButton to="/dashboard" />
        </div>

        {/* Header */}
        <div className="mt-6 mb-6 bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <input
                ref={nameInputRef}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={handleNameBlur}
                className="text-3xl font-bold text-gray-900 w-full border-0 border-b-2 border-transparent hover:border-gray-300 focus:border-primary-500 focus:outline-none px-0 py-1 transition-colors bg-transparent"
                placeholder="Gallery name"
              />
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={handleDescriptionBlur}
                rows="2"
                className="text-gray-600 mt-2 w-full border-0 border-b-2 border-transparent hover:border-gray-300 focus:border-primary-500 focus:outline-none px-0 py-1 transition-colors resize-none bg-transparent"
                placeholder="Add a description..."
              />
            </div>
            <div className="flex items-center gap-4 flex-shrink-0">
              <SaveStatusIndicator status={saveStatus} />
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-500'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                  </svg>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Videos Tab */}
            {activeTab === 'videos' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Add Videos</h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {videoLinks.length} {videoLinks.length === 1 ? 'video' : 'videos'} added
                    </p>
                  </div>
                  <button
                    onClick={() => setShowAddVideoModal(true)}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 font-medium flex items-center gap-2 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Video
                  </button>
                </div>

                {videoLinks.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No videos yet</h3>
                    <p className="text-gray-600 mb-4">Add your first video to get started</p>
                    <button
                      onClick={() => setShowAddVideoModal(true)}
                      className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 font-medium"
                    >
                      Add Your First Video
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {videoLinks.map((video) => (
                      <VideoThumbnailCard
                        key={video.id}
                        video={video}
                        onUpdate={(_, data) => handleUpdateVideo(video.id, data)}
                        onDelete={() => handleDeleteVideo(video.id)}
                        isEditing={true}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'general' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Privacy Settings</h2>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isPrivate}
                        onChange={(e) => handlePrivacyChange(e.target.checked)}
                        className="mt-1 w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <span className="ml-3">
                        <span className="font-medium text-gray-900 block">Invite only</span>
                        <span className="text-sm text-gray-600">
                          Only people you invite can access this gallery
                        </span>
                      </span>
                    </label>
                  </div>
                </div>

                {isPrivate ? (
                  <div className="border-t pt-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Invited Users</h2>
                    <InvitationInput
                      galleryId={galleryId}
                      invitations={invitations}
                      onInvitationAdded={handleInvitationAdded}
                      onInvitationRemoved={handleInvitationRemoved}
                      onInvitationUpdated={handleInvitationUpdated}
                      disabled={!galleryId}
                    />
                  </div>
                ) : (
                  <div className="border-t pt-6">
                    <div className="text-center py-8 bg-primary-50 rounded-lg border border-primary-200">
                      <svg className="w-12 h-12 text-primary-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <h3 className="text-base font-medium text-gray-900 mb-1">Anyone with the link</h3>
                      <p className="text-sm text-gray-600">Anyone who has the link can view this gallery</p>
                    </div>
                  </div>
                )}

                {/* Video Settings Section */}
                <div className="border-t pt-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Video Settings</h2>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="checkbox"
                        checked={addNewVideosAtBeginning}
                        onChange={(e) => handleVideoOrderChange(e.target.checked)}
                        className="mt-1 w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <span className="ml-3">
                        <span className="font-medium text-gray-900 block">Add new videos at the beginning</span>
                        <span className="text-sm text-gray-600">
                          New videos will appear at the start of the gallery
                        </span>
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Video Modal */}
      <AddVideoModal
        isOpen={showAddVideoModal}
        onClose={() => setShowAddVideoModal(false)}
        onAdd={handleAddVideo}
      />
    </div>
  );
}

export default NewGalleryPage;
